import { useContext, useState } from 'react'

import _ from 'lodash'
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  DiceFaces06Icon,
  FloppyDiskIcon
} from '@hugeicons/react'
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu'
import usePreventBodyScroll from '@/shared/hooks/usePreventBodyScroll'
import { Card } from '@/shared/ui'
import { AppIcon } from '@/shared/ui/icons'

import { defaultOptions, getRandomConfig, configHasChanges } from '../utils'
import { AvatarEditorColorSelector } from './ColorSelector'
import { AvatarEditorIcons } from './Icons'
import styles from './styles.module.scss'
import { AvatarEditorStyleSelector } from './StyleSelector'

export const AvatarEditor = ({
  initialConfig = {
    body: {},
    hair: {},
    eyes: {},
    shirt: {},
    bottom: {}
  },
  showCancelButton = false,
  onCancel,
  onChange,
  onSave
}) => {
  const { disableScroll, enableScroll } = usePreventBodyScroll()
  const options = Object.keys(defaultOptions).map(key => ({
    slug: key,
    ...defaultOptions[key]
  }))
  const [config, setConfig] = useState(initialConfig)
  const [selectedOptionSlug, setSelectedOptionSlug] = useState(
    _.get(options, '0.slug')
  )
  const selectedOption = options.find(
    option => option.slug === selectedOptionSlug
  )

  const handleSelectStyle = (slug, style) => {
    let newConfig = { ...config }

    _.set(newConfig, `${slug}.style`, style)

    setConfig(newConfig)
    onChange(newConfig)
  }
  const handleSelectColor = (slug, color) => {
    const newConfig = { ...config }

    _.set(newConfig, `${slug}.color`, color)

    if (slug === 'body') _.set(newConfig, 'head.color', color)

    setConfig(newConfig)
    onChange(newConfig)
  }
  const handleRandomize = () => {
    const newConfig = getRandomConfig()

    setConfig(newConfig)
    onChange(newConfig)
  }
  const handleSave = () =>
    onSave(config, configHasChanges(config, initialConfig))

  return (
    <div className="flex flex-col gap-4 w-full overflow-hidden shrink-0">
      <div
        id="avatar-editor-options"
        className={styles.DisableScrollBar}
        onMouseEnter={disableScroll}
        onMouseLeave={enableScroll}
      >
        <ScrollMenu
          LeftArrow={LeftArrow}
          RightArrow={RightArrow}
          onWheel={onWheel}
          scrollContainerClassName="flex gap-2"
        >
          {options.map(option => {
            const Icon = option.customIcon
            const isSelected = selectedOptionSlug == option.slug

            return (
              <div key={option.slug}>
                <button
                  className={`flex items-center justify-center w-[50px] h-[50px] bg-white rounded-lg border-0 cursor-pointer transition-colors ${
                    isSelected ? 'bg-info' : 'hover:bg-info/10'
                  }`}
                  title={option.name}
                  type="button"
                  onClick={() => setSelectedOptionSlug(option.slug)}
                >
                  {['footer', 'bottom'].includes(option.slug) ? (
                    <AvatarEditorIcons {...option} isSelected={isSelected} />
                  ) : (
                    <AppIcon
                      name={option.icon}
                      className={`text-2xl ${
                        isSelected ? 'text-white' : 'text-info'
                      }`}
                    />
                  )}
                </button>
              </div>
            )
          })}
        </ScrollMenu>
      </div>
      <Card className="border-2 animate__animated animate__fadeIn">
        <Card.Body className="flex flex-col p-0 min-h-[250px] lg:min-h-[400px]">
          {selectedOption && (
            <>
              <AvatarEditorStyleSelector
                selectedOption={selectedOption}
                styles={_.get(selectedOption, 'styles')}
                selectedStyle={_.get(config, `${selectedOption.slug}.style`)}
                selectedColor={_.get(config, `${selectedOption.slug}.color`)}
                className="p-6 lg:p-8"
                onSelectStyle={handleSelectStyle}
              />
              <div className="mt-auto border-t border-border">
                {_.get(selectedOption, 'colors') && (
                  <AvatarEditorColorSelector
                    selectedOption={selectedOption}
                    colors={_.get(selectedOption, 'colors')}
                    selectedColor={_.get(
                      config,
                      `${selectedOption.slug}.color`
                    )}
                    onSelectColor={handleSelectColor}
                  />
                )}

                <div className="flex justify-between items-center bg-gray-100 p-6 lg:p-8 rounded-b-[0.9rem]">
                  <button
                    id="avatar-editor-randomize-button"
                    type="button"
                    className="flex items-center gap-1 shrink-0 px-2 text-sm font-bold text-gray-700 hover:text-info bg-transparent border-0 cursor-pointer"
                    onClick={handleRandomize}
                  >
                    <DiceFaces06Icon variant="duotone" size={20} /> Gerar aleatório
                  </button>
                  <div className="flex items-center justify-center gap-2">
                    {showCancelButton && (
                      <button
                        id="avatar-editor-cancel-button"
                        type="button"
                        className="flex items-center h-8 px-3 rounded-[10px] bg-gray-100 text-gray-800 font-bold text-sm border border-gray-300 cursor-pointer hover:bg-gray-200"
                        onClick={onCancel}
                      >
                        cancelar
                      </button>
                    )}
                    <button
                      id="avatar-editor-save-button"
                      type="button"
                      className="flex items-center gap-1 h-8 px-3 rounded-[10px] bg-info/10 text-info font-bold text-sm border-0 cursor-pointer hover:bg-info/15"
                      onClick={handleSave}
                    >
                      <FloppyDiskIcon variant="duotone" size={18} /> Salvar{' '}
                      <span className="hidden sm:inline-block">avatar</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

const LeftArrow = () => {
  const visibility = useContext(VisibilityContext)
  const isVisible = visibility.useIsVisible('first', false)

  return (
    <Arrow
      disabled={isVisible}
      onClick={() => visibility.scrollPrev()}
      className="mr-2"
    >
      <ArrowLeft01Icon variant="duotone" size={18} />
    </Arrow>
  )
}

const RightArrow = () => {
  const visibility = useContext(VisibilityContext)
  const isVisible = visibility.useIsVisible('last', false)

  return (
    <Arrow
      disabled={isVisible}
      onClick={() => visibility.scrollNext()}
      className="ml-2"
    >
      <ArrowRight01Icon variant="duotone" size={18} />
    </Arrow>
  )
}

const Arrow = ({ children, disabled, onClick, className = '' }) => (
  <button
    disabled={disabled}
    onClick={onClick}
    className={`flex items-center justify-center h-full w-8 rounded-[10px] bg-info/10 text-info border-0 cursor-pointer hover:bg-info/15 disabled:opacity-40 disabled:cursor-default ${className}`}
  >
    {children}
  </button>
)

const onWheel = (apiObj, ev) => {
  const isThouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15

  if (isThouchpad) {
    ev.stopPropagation()
    return
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext()
  } else {
    apiObj.scrollPrev()
  }
}
